import { useCallback, useMemo } from 'react'

import useAuth from '../../../../base/hooks/useAuth'
import { useWaapiEnviarUrl } from '../api/apiWaapiEnviarUrl.ts'
import type { WaapiEnviarUrlProps } from '../interfaces/waapi.ts'
import { esUrlPublicaWaapi, WAAPI_MENSAJE_SIN_LICENCIA, WaapiError } from '../services/waapiError.ts'
import {
  generarMensajeWaapi,
  nombreArchivoWaapi,
  WaapiDocumento,
  WaapiMensajeParams,
} from '../services/waapiMensaje.ts'
import { parseTelefonoWaapi } from '../services/waapiTelefono.ts'

export interface WaapiEnviarParams {
  /** Teléfono tal como lo escribió el usuario o está registrado (acepta +código de país) */
  telefono: string
  /** Código de país sin '+' si el teléfono no lo incluye. Por defecto 591 */
  codigoPais?: string | null
  /** URL pública (http/https) del archivo, ej. representación gráfica en PDF */
  url: string
  /** Si no se indica, se genera un mensaje dinámico con `documento`, `numero` y `destinatario` */
  mensaje?: string | null
  documento?: WaapiDocumento
  /** Reemplaza la frase del documento en el mensaje, ej. "el reporte de cierre de la Caja 2" */
  descripcion?: string | null
  numero?: string | number | null
  /** Nombre de quien recibe (cliente o usuario), para el saludo y el nombre del archivo */
  destinatario?: string | null
  /** Nombre del archivo que ve quien recibe. Por defecto "Factura 123 - Juan Perez" */
  nombreArchivo?: string | null
}

export interface WaapiDestino {
  telefono: string
  codigoPais?: string | null
  destinatario?: string | null
}

export interface WaapiEnvioMultipleResultado {
  enviados: WaapiEnviarUrlProps[]
  fallidos: { destino: WaapiDestino; error: unknown }[]
}

/** Pausa entre envíos a varios destinos, para no enviar en ráfaga */
const pausa = () => new Promise((r) => setTimeout(r, 1_000 + Math.random() * 1_500))

/**
 * Envío de archivos por WhatsApp (Waapi) con la licencia `lw` de la sesión.
 * Cada envío lleva un mensaje distinto (ver `generarMensajeWaapi`) salvo que se indique `mensaje`.
 * @example
 * const waapi = useWaapi()
 * if (waapi.licenciaActiva) {
 *   await waapi.enviar({
 *     telefono: cliente.telefono,
 *     url: factura.representacionGrafica.pdf,
 *     documento: 'FACTURA',
 *     numero: factura.numeroFactura,
 *     destinatario: cliente.razonSocial,
 *   })
 * }
 */
export const useWaapi = () => {
  const { user, lw } = useAuth()
  const licenciaActiva = Boolean(lw?.activo)
  const empresa =
    user?.miEmpresa?.denominacion?.trim() ||
    user?.miEmpresa?.razonSocial?.trim() ||
    user?.razonSocial?.trim() ||
    ''

  const mutation = useWaapiEnviarUrl()
  const { mutateAsync } = mutation

  /** Mensaje dinámico con el nombre del comercio de la sesión */
  const generarMensaje = useCallback(
    (params: WaapiMensajeParams = {}) => generarMensajeWaapi({ empresa, ...params }),
    [empresa],
  )

  const enviar = useCallback(
    async (params: WaapiEnviarParams): Promise<WaapiEnviarUrlProps> => {
      if (!licenciaActiva) throw new WaapiError('SIN_LICENCIA', WAAPI_MENSAJE_SIN_LICENCIA)
      if (user?.sucursal?.codigo == null || user?.puntoVenta?.codigo == null) {
        throw new WaapiError('SIN_SESION', 'No hay una sucursal y punto de venta en la sesión')
      }
      const tel = parseTelefonoWaapi(params.telefono, params.codigoPais)
      if (!tel) {
        throw new WaapiError(
          'TELEFONO_INVALIDO',
          `El número de WhatsApp "${params.telefono ?? ''}" no es válido`,
        )
      }
      if (!esUrlPublicaWaapi(params.url)) {
        throw new WaapiError(
          'URL_INVALIDA',
          'El documento no tiene un enlace público; no se puede enviar por WhatsApp',
        )
      }
      const mensaje =
        params.mensaje?.trim() ||
        generarMensaje({
          documento: params.documento,
          descripcion: params.descripcion,
          numero: params.numero,
          destinatario: params.destinatario,
        })
      return mutateAsync({
        entidad: { codigoSucursal: user.sucursal.codigo, codigoPuntoVenta: user.puntoVenta.codigo },
        input: {
          codigoArea: tel.codigoArea,
          telefono: tel.telefono,
          mensaje,
          url: params.url,
          nombre:
            params.nombreArchivo?.trim() ||
            nombreArchivoWaapi({
              documento: params.documento,
              numero: params.numero,
              destinatario: params.destinatario,
            }),
        },
      })
    },
    [licenciaActiva, user, generarMensaje, mutateAsync],
  )

  /**
   * Envía el mismo archivo a varios destinos, uno por uno y con una pausa entre envíos.
   * Cada destino recibe un mensaje distinto (generado con su nombre). No lanza errores por destino.
   */
  const enviarVarios = useCallback(
    async (
      destinos: WaapiDestino[],
      comun: Omit<WaapiEnviarParams, 'telefono' | 'codigoPais' | 'destinatario' | 'mensaje'>,
    ): Promise<WaapiEnvioMultipleResultado> => {
      if (!licenciaActiva) throw new WaapiError('SIN_LICENCIA', WAAPI_MENSAJE_SIN_LICENCIA)
      const r: WaapiEnvioMultipleResultado = { enviados: [], fallidos: [] }
      for (let i = 0; i < destinos.length; i++) {
        if (i > 0) await pausa()
        const destino = destinos[i]
        try {
          r.enviados.push(await enviar({ ...comun, ...destino }))
        } catch (error) {
          r.fallidos.push({ destino, error })
        }
      }
      return r
    },
    [licenciaActiva, enviar],
  )

  return useMemo(
    () => ({
      /** Licencia de WhatsApp vigente (`lw.activo` de la sesión) */
      licenciaActiva,
      /** Motivo legible cuando no hay licencia */
      licenciaMensaje: licenciaActiva ? null : WAAPI_MENSAJE_SIN_LICENCIA,
      /** Vencimiento de la licencia, si el backend lo informa */
      licenciaVencimiento: lw?.licencia?.fechaVencimiento ?? null,
      empresa,
      enviar,
      enviarVarios,
      generarMensaje,
      enviando: mutation.isPending,
    }),
    [licenciaActiva, lw, empresa, enviar, enviarVarios, generarMensaje, mutation.isPending],
  )
}

export type UseWaapiResult = ReturnType<typeof useWaapi>

export default useWaapi
